import { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { updateSubject, getSubjectById } from "../../../api/pedagogicElement";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  subjectID: UUID;
  onClose: () => void;
  onSaved?: () => void;
};

const EditSubjectModal = ({ subjectID, show, onClose, onSaved }: Props) => {
  //Schéma de notre formulaire
  const createSchema = z.object({
    code: z.string().nonempty("Le code de la matière est requis"),
    label: z.string().nonempty("Le libellé de la matière est requis"),
    to_ignore: z.boolean(),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await updateSubject(data, subjectID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Matière mise à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !subjectID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchSubject = async () => {
      try {
        const response = await getSubjectById(
          subjectID,
          buildPermissionContext(Resource.SUBJECT, Action.UPDATE),
        );
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Matière introuvable");
        }

        reset({
          // Si année trouvé on rempli les champs du formulaire
          code: response.data.code,
          label: response.data.label,
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
          error.message ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
      }
    };

    fetchSubject();
  }, [show, subjectID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifer une Matière</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    CODE de la matière
                  </label>
                  <input
                    {...register("code")}
                    type="text"
                    className={
                      errors.code ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: AGL - MTS - CPT"
                  />
                  {errors.code && (
                    <div className="invalid-feedback">
                      {errors.code.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Anglais, Mathématiques"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="m-auto flex-fill ">
                <div className="form-check form-check-lg form-switch">
                  <label className="form-check-label">Ignorer ?</label>
                  <input
                    {...register("to_ignore")}
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="switch-lg"
                  />
                </div>
                {errors.to_ignore && (
                  <div className="invalid-feedback">
                    {errors.to_ignore.message}
                  </div>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditSubjectModal,
  [buildPermission(Resource.SUBJECT, Action.UPDATE)],
  "masquer",
);
